import { useEffect, useState, useCallback, useRef } from 'react';
import { usePsycheStore } from '../../stores/psycheStore';
import { useCampaignStore } from '../../stores/campaignStore';
import { useCharacterStore } from '../../stores/characterStore';
import { useAuthStore } from '../../stores/authStore';
import { Icon } from '../Icon';
import { PSYCHE_VOICE_META } from '@machina/core';
import type { DeliveredThought, ThoughtIntensity } from '@machina/core';

/**
 * PsycheThoughtDisplay -- Player-facing psyche thought overlay
 *
 * Displays delivered thoughts as floating toast cards.
 * Intensity styles: whisper (translucent, italic), thought (standard), shout (bold banner).
 * Includes a collapsible "Thought Journal" for reviewing past thoughts.
 */

// How long each toast stays visible (ms) before auto-dismiss
const AUTO_DISMISS_MS: Record<ThoughtIntensity, number> = {
  whisper: 6000,
  thought: 10000,
  shout: 15000,
};

export function PsycheThoughtDisplay() {
  const campaignId = useCampaignStore((s) => s.currentCampaignId);
  const user = useAuthStore((s) => s.user);
  const partyIds = useCharacterStore((s) => s.partyIds);
  const characters = useCharacterStore((s) => s.characters);

  const {
    deliveredThoughts,
    loadDelivered,
    markRead,
    subscribeToRealtime,
    unsubscribeFromRealtime,
  } = usePsycheStore();

  // Active toasts (thoughts currently being displayed)
  const [activeToasts, setActiveToasts] = useState<DeliveredThought[]>([]);
  const [journalOpen, setJournalOpen] = useState(false);
  const prevDeliveredRef = useRef<string[]>([]);

  // Find the player's character (first party character belonging to this user)
  const myCharacterId = (() => {
    if (!user) return null;
    for (const id of partyIds) {
      const char = characters.get(id);
      if (char) return char.id;
    }
    return null;
  })();

  // Subscribe to realtime + load existing delivered thoughts
  useEffect(() => {
    if (!campaignId || !myCharacterId) return;
    loadDelivered(campaignId, myCharacterId);
    subscribeToRealtime(campaignId, myCharacterId);
    return () => unsubscribeFromRealtime();
  }, [campaignId, myCharacterId]);

  // Detect new thoughts and create toasts
  useEffect(() => {
    const currentIds = deliveredThoughts
      .filter((t) => !t.wasRead)
      .map((t) => t.thoughtId);

    const newThoughts = deliveredThoughts.filter(
      (t) => !t.wasRead && !prevDeliveredRef.current.includes(t.thoughtId)
    );

    if (newThoughts.length > 0) {
      setActiveToasts((prev) => {
        const existingIds = new Set(prev.map((t) => t.thoughtId));
        const toAdd = newThoughts.filter((t) => !existingIds.has(t.thoughtId));
        return [...prev, ...toAdd];
      });
    }

    prevDeliveredRef.current = currentIds;
  }, [deliveredThoughts]);

  // Auto-dismiss toasts
  const dismissToast = useCallback(
    (thoughtId: string) => {
      setActiveToasts((prev) => prev.filter((t) => t.thoughtId !== thoughtId));
      markRead(thoughtId);
    },
    [markRead]
  );

  // Nothing to show if no character
  if (!myCharacterId) return null;

  const unreadCount = deliveredThoughts.filter((t) => !t.wasRead).length;

  return (
    <>
      {/* Floating toasts */}
      <div
        style={{
          position: 'fixed',
          top: 70,
          right: 340, // Right of chat sidebar
          width: 320,
          maxHeight: 'calc(100vh - 140px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          zIndex: 900,
          pointerEvents: 'none',
        }}
      >
        {activeToasts.map((thought) => (
          <ThoughtToast
            key={thought.thoughtId}
            thought={thought}
            onDismiss={dismissToast}
          />
        ))}
      </div>

      {/* Thought journal toggle (bottom-right, above chat) */}
      {deliveredThoughts.length > 0 && (
        <button
          onClick={() => setJournalOpen(!journalOpen)}
          title="Thought Journal"
          style={{
            position: 'fixed',
            bottom: 16,
            right: 336,
            width: 36,
            height: 36,
            borderRadius: '50%',
            backgroundColor: journalOpen ? 'var(--color-accent)' : 'var(--color-surface-elevated)',
            border: `2px solid ${journalOpen ? 'var(--color-accent)' : 'var(--color-border)'}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: journalOpen ? 'var(--color-surface)' : 'var(--color-text)',
            zIndex: 901,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          <Icon name="brain" size={16} />
          {unreadCount > 0 && (
            <span
              style={{
                position: 'absolute',
                top: -4,
                right: -4,
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: 'var(--color-accent)',
                color: 'var(--color-surface)',
                fontSize: 10,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Thought journal panel */}
      {journalOpen && (
        <ThoughtJournal
          thoughts={deliveredThoughts}
          onClose={() => setJournalOpen(false)}
          onMarkRead={markRead}
        />
      )}
    </>
  );
}

// =============================================================================
// THOUGHT TOAST
// =============================================================================

function ThoughtToast({
  thought,
  onDismiss,
}: {
  thought: DeliveredThought;
  onDismiss: (id: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const meta = PSYCHE_VOICE_META[thought.voice];
  const intensity = thought.intensity;

  // Animate in
  useEffect(() => {
    const enterTimer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(enterTimer);
  }, []);

  // Auto-dismiss
  useEffect(() => {
    const duration = AUTO_DISMISS_MS[intensity];
    const fadeTimer = setTimeout(() => setFading(true), duration - 500);
    const dismissTimer = setTimeout(() => onDismiss(thought.thoughtId), duration);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(dismissTimer);
    };
  }, [intensity, thought.thoughtId, onDismiss]);

  const handleClick = () => {
    setFading(true);
    setTimeout(() => onDismiss(thought.thoughtId), 300);
  };

  // Intensity-specific styles
  const intensityStyles = getIntensityStyles(intensity, meta.color, visible, fading);

  return (
    <div
      onClick={handleClick}
      style={{
        ...intensityStyles,
        pointerEvents: 'auto',
        cursor: 'pointer',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        transition: 'all 0.4s ease',
      }}
    >
      {/* Voice label */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: intensity === 'whisper' ? 2 : 4,
        }}
      >
        <span
          style={{
            fontSize: intensity === 'shout' ? 'var(--font-size-sm)' : 'var(--font-size-xs)',
            color: meta.color,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {meta.label}
        </span>
        {intensity === 'shout' && (
          <span style={{ fontSize: 10, color: meta.color, opacity: 0.7 }}>
            demands your attention
          </span>
        )}
      </div>

      {/* Content */}
      <div
        style={{
          fontSize: intensity === 'shout' ? 'var(--font-size-md)' : 'var(--font-size-sm)',
          color: intensity === 'whisper' ? 'var(--color-text-secondary)' : 'var(--color-text)',
          fontStyle: intensity === 'whisper' ? 'italic' : 'normal',
          fontWeight: intensity === 'shout' ? 600 : 400,
          lineHeight: 1.5,
        }}
      >
        {thought.content}
      </div>

      {/* Click to dismiss hint */}
      <div
        style={{
          fontSize: 9,
          color: 'var(--color-text-muted)',
          textAlign: 'right',
          marginTop: 4,
          opacity: 0.6,
        }}
      >
        click to dismiss
      </div>
    </div>
  );
}

function getIntensityStyles(
  intensity: ThoughtIntensity,
  voiceColor: string,
  visible: boolean,
  fading: boolean
): React.CSSProperties {
  const opacity = fading ? 0 : visible ? 1 : 0;
  const translateY = visible && !fading ? 0 : -20;

  const base: React.CSSProperties = {
    opacity,
    transform: `translateY(${translateY}px)`,
  };

  switch (intensity) {
    case 'whisper':
      return {
        ...base,
        padding: '8px 12px',
        backgroundColor: `color-mix(in srgb, var(--color-surface-elevated) 80%, transparent)`,
        border: `1px solid ${voiceColor}30`,
        borderLeft: `2px solid ${voiceColor}60`,
        backdropFilter: 'blur(8px)',
        opacity: fading ? 0 : visible ? 0.85 : 0,
      };

    case 'thought':
      return {
        ...base,
        padding: '10px 14px',
        backgroundColor: 'var(--color-surface-elevated)',
        border: `1px solid ${voiceColor}50`,
        borderLeft: `3px solid ${voiceColor}`,
        boxShadow: `0 4px 16px rgba(0,0,0,0.3), 0 0 8px ${voiceColor}20`,
      };

    case 'shout':
      return {
        ...base,
        padding: '14px 16px',
        backgroundColor: 'var(--color-surface-elevated)',
        border: `2px solid ${voiceColor}`,
        boxShadow: `0 4px 24px rgba(0,0,0,0.4), 0 0 20px ${voiceColor}30, inset 0 0 40px ${voiceColor}08`,
      };
  }
}

// =============================================================================
// THOUGHT JOURNAL
// =============================================================================

function ThoughtJournal({
  thoughts,
  onClose,
  onMarkRead,
}: {
  thoughts: DeliveredThought[];
  onClose: () => void;
  onMarkRead: (id: string) => void;
}) {
  // Sort by delivery time, most recent first
  const sorted = [...thoughts].sort(
    (a, b) => new Date(b.deliveredAt).getTime() - new Date(a.deliveredAt).getTime()
  );

  return (
    <div
      style={{
        position: 'fixed',
        top: 60,
        right: 330,
        width: 320,
        maxHeight: 'calc(100vh - 120px)',
        backgroundColor: 'var(--color-surface)',
        border: '2px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 902,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: 'var(--spacing-sm) var(--spacing-md)',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'var(--color-background-elevated)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="brain" size={16} color="var(--color-accent)" />
          <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
            Thought Journal
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
            }}
          >
            ({thoughts.length})
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            padding: 4,
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--color-text-secondary)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Icon name="close" size={14} />
        </button>
      </div>

      {/* Thought list */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--spacing-xs)',
        }}
      >
        {sorted.length === 0 ? (
          <div
            style={{
              padding: 'var(--spacing-md)',
              textAlign: 'center',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-muted)',
            }}
          >
            No thoughts yet. Your inner voices are quiet...
          </div>
        ) : (
          sorted.map((thought) => (
            <JournalEntry
              key={thought.thoughtId}
              thought={thought}
              onMarkRead={onMarkRead}
            />
          ))
        )}
      </div>
    </div>
  );
}

function JournalEntry({
  thought,
  onMarkRead,
}: {
  thought: DeliveredThought;
  onMarkRead: (id: string) => void;
}) {
  const meta = PSYCHE_VOICE_META[thought.voice];
  const timeAgo = formatTimeAgo(thought.deliveredAt);

  return (
    <div
      onClick={() => {
        if (!thought.wasRead) onMarkRead(thought.thoughtId);
      }}
      style={{
        padding: '8px 10px',
        marginBottom: 4,
        borderRadius: 'var(--radius-sm)',
        backgroundColor: thought.wasRead
          ? 'transparent'
          : 'var(--color-surface-elevated)',
        borderLeft: `3px solid ${thought.wasRead ? meta.color + '40' : meta.color}`,
        cursor: thought.wasRead ? 'default' : 'pointer',
        opacity: thought.wasRead ? 0.7 : 1,
        transition: 'opacity 0.2s ease',
      }}
    >
      {/* Voice + time */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 2,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: meta.color,
              fontWeight: 600,
            }}
          >
            {meta.label}
          </span>
          <span
            style={{
              fontSize: 9,
              color: 'var(--color-text-muted)',
              textTransform: 'uppercase',
            }}
          >
            {thought.intensity}
          </span>
        </div>
        <span
          style={{
            fontSize: 9,
            color: 'var(--color-text-muted)',
          }}
        >
          {timeAgo}
        </span>
      </div>

      {/* Content */}
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          color: thought.wasRead ? 'var(--color-text-secondary)' : 'var(--color-text)',
          fontStyle: thought.intensity === 'whisper' ? 'italic' : 'normal',
          lineHeight: 1.4,
        }}
      >
        {thought.content}
      </div>

      {/* Unread indicator */}
      {!thought.wasRead && (
        <div
          style={{
            fontSize: 9,
            color: 'var(--color-text-muted)',
            textAlign: 'right',
            marginTop: 2,
          }}
        >
          click to mark read
        </div>
      )}
    </div>
  );
}

// =============================================================================
// HELPERS
// =============================================================================

function formatTimeAgo(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;

  if (diffMs < 60_000) return 'just now';
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`;
  return `${Math.floor(diffMs / 86_400_000)}d ago`;
}
